import { Injectable } from '@angular/core';
import { CanvasBranch, Position } from './canvas-workflow.interface';

@Injectable({
  providedIn: 'root'
})
export class PositionCalculationService {

  readonly COMPONENT_WIDTH = 350;
  /** Hauteur retenue pour un bloc tant qu'il n'a pas été mesuré : la hauteur réelle suit son contenu. */
  readonly COMPONENT_HEIGHT = 100;
  readonly NODE_SPACING = 20;
  readonly BRANCH_PADDING = 20;
  readonly COLUMN_WIDTH = 400;
  readonly PARENT_CHILD_SPACING = 120;

  private currentColumn: number = 0;

  /**
 * Calcule toutes les positions et dimensions des branches
 * @param nodeHeights hauteur mesurée de chaque bloc, par id de nœud
 */
  updateBranchesBounds(rootBranch: CanvasBranch, nodeHeights: Map<string, number> = new Map()): void {
    this.currentColumn = 0;
    this.assignColumnsToLeaves(rootBranch, 0);
    this.calculateInternalBranchColumns(rootBranch);
    this.calculateBranchBounds(rootBranch, nodeHeights);
  }

  /**
   * Assigne des colonnes aux branches feuilles
   */
  private assignColumnsToLeaves(branch: CanvasBranch, startRow: number): void {
    branch.startRow = startRow;
    const branchHeight = Math.max(1, branch.nodes.length);
    branch.endRow = startRow + branchHeight - 1;

    if (branch.children.length === 0) {
      branch.leftmostColumn = this.currentColumn;
      branch.rightmostColumn = this.currentColumn;
      branch.centerColumn = this.currentColumn;
      this.currentColumn++;
      return;
    }

    branch.children.forEach(child => {
      this.assignColumnsToLeaves(child, branch.endRow + 1);
    });
  }

  /**
   * Calcule les colonnes pour les branches internes
   */
  private calculateInternalBranchColumns(branch: CanvasBranch): void {
    if (branch.children.length === 0) return;

    branch.children.forEach(child => this.calculateInternalBranchColumns(child));

    const children = branch.children;

    if (children.length === 2) {
      branch.leftmostColumn = Math.min(children[0].leftmostColumn, children[1].leftmostColumn);
      branch.rightmostColumn = Math.max(children[0].rightmostColumn, children[1].rightmostColumn);
      branch.centerColumn = (children[0].centerColumn + children[1].centerColumn) / 2;
    } else if (children.length === 1) {
      branch.leftmostColumn = children[0].leftmostColumn;
      branch.rightmostColumn = children[0].rightmostColumn;
      branch.centerColumn = children[0].centerColumn;
    }
  }

  /**
   * Calcule les positions et dimensions finales des branches
   */
  private calculateBranchBounds(branch: CanvasBranch, nodeHeights: Map<string, number>, parentBranch?: CanvasBranch): void {
    branch.position.x = 50 + branch.centerColumn * this.COLUMN_WIDTH;

    if (!parentBranch) {
      branch.position.y = 50;
    } else {
      branch.position.y = parentBranch.position.y + parentBranch.height + this.PARENT_CHILD_SPACING;
    }

    branch.width = this.COMPONENT_WIDTH + (this.BRANCH_PADDING * 2);

    const nodeCount = branch.nodes.length;
    const nodesHeight = branch.nodes.reduce(
      (sum, node) => sum + (nodeHeights.get(node.id) ?? this.COMPONENT_HEIGHT), 0);
    branch.height = (this.BRANCH_PADDING * 2) + nodesHeight +
      ((nodeCount - 1) * this.NODE_SPACING);

    branch.children.forEach(child => this.calculateBranchBounds(child, nodeHeights, branch));
  }

  /**
   * Calcule la taille nécessaire du canvas en fonction de toutes les branches
   */
  calculateCanvasSize(allBranches: CanvasBranch[], viewportSize: { width: number, height: number }, zoomLevel: number = 1): { width: number, height: number } {
    if (allBranches.length === 0) {
      return {
        width: Math.max(viewportSize.width, viewportSize.width / zoomLevel),
        height: Math.max(viewportSize.height, viewportSize.height / zoomLevel)
      };
    }

    // Calculer la taille minimale basée sur le contenu
    const maxX = Math.max(...allBranches.map(b => b.position.x + b.width));
    const maxY = Math.max(...allBranches.map(b => b.position.y + b.height));

    const contentWidth = maxX + 100; // padding
    const contentHeight = maxY + 100; // padding

    // Calculer la taille minimale pour couvrir le viewport à tous les niveaux de zoom
    const minWidthForViewport = viewportSize.width / zoomLevel;
    const minHeightForViewport = viewportSize.height / zoomLevel;

    return {
      width: Math.max(contentWidth, viewportSize.width, minWidthForViewport),
      height: Math.max(contentHeight, viewportSize.height, minHeightForViewport)
    };
  }
}

